import type { AxiosProgressEvent } from 'axios';
import axios from 'axios';
import type { z } from 'zod';

import type { ExcelError, ExcelResult } from '@apple/utils/files/models/excel';
import {
	createExcelErrorSchema,
	createExcelResultSchema,
	EXCEL_CONTENT_TYPE_HEADER,
} from '@apple/utils/files/models/excel';
import { encodeSlashes } from '@apple/utils/url';

import type { ProductAudit } from '@apple/features/product/models/management';
import {
	excelProductKeySchema,
	productAuditSchema,
} from '@apple/features/product/models/management';
import type { ProductKey } from '@apple/features/product/models/product';

import type { AppleBrandedProduct } from '../models/management';
import { appleBrandedProductSchema } from '../models/management';

/** Service wrapper for:
 * - AppleBrandedPrograms.Web.Features.Catalog.AppleBrandedProductsController
 * - Manzanita.Web.Features.Catalog.ProductsController
 * - Manzanita.Web.Features.Catalog.ProductImagesController
 */

/**
 * Gets the product with the given ID.
 * @see AppleBrandedPrograms.Web.Features.Catalog.AppleBrandedProductsController
 */
export async function getProduct(itemId: ProductKey): Promise<AppleBrandedProduct> {
	const response = await axios.get(`/api/management/products/${encodeSlashes(itemId)}`);
	return appleBrandedProductSchema.parse(response.data);
}

/**
 * Saves a new or existing product with the given ID and information.
 * @param product - The product to save.
 * @param auditComment - Optional. Only required if the audit filter is enabled on the server.
 * @see AppleBrandedPrograms.Web.Features.Catalog.AppleBrandedProductsController
 */
export async function saveProduct(
	product: AppleBrandedProduct,
	auditComment?: string,
): Promise<void> {
	await axios.put(`/api/management/products/${encodeSlashes(product.itemId)}`, product, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/**
 * Saves a collection of new, existing, or deleted products.
 * @param products - The products to save.
 * @param auditComment - Optional. Only required if the audit filter is enabled on the server.
 * @see AppleBrandedPrograms.Web.Features.Catalog.AppleBrandedProductsController
 */
export async function saveProducts(
	products: AppleBrandedProduct[],
	auditComment?: string,
): Promise<void> {
	if (products.length === 0) {
		return;
	}

	await axios.post('/api/management/products', products, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/**
 * Deletes the product with the given ID.
 * @see AppleBrandedPrograms.Web.Features.Catalog.AppleBrandedProductsController
 */
export async function deleteProduct(itemId: ProductKey, auditComment?: string): Promise<void> {
	await axios.delete(`/api/management/products/${encodeSlashes(itemId)}`, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/**
 * Deletes the products with the given IDs.
 * @see AppleBrandedPrograms.Web.Features.Catalog.AppleBrandedProductsController
 */
export async function deleteProducts(itemIds: ProductKey[], auditComment?: string): Promise<void> {
	if (itemIds.length === 0) {
		return;
	}

	await axios.post('/api/management/products/delete-multiple', itemIds, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/**
 * This method is implemented in the client projects, but the URL is the same in both projects.
 * @see AppleBrandedPrograms.Web.Features.Catalog.AppleBrandedProductsController
 */
export async function getProductAudits(itemId: ProductKey): Promise<ProductAudit[]> {
	const response = await axios.get(`/api/management/products/${encodeSlashes(itemId)}/audits`);
	return productAuditSchema.array().parse(response.data);
}

/**
 * Uploads a product bulk management Excel file to be converted into products.
 * @see AppleBrandedPrograms.Web.Features.Catalog.ProductsExcelController
 */
export async function uploadProducts(
	file: File,
	onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<ProductExcelResult> {
	const response = await axios.post<unknown>('/api/management/products/bulk/excel', file, {
		headers: {
			...EXCEL_CONTENT_TYPE_HEADER,
		},
		onUploadProgress,
	});

	return productExcelResultSchema.parse(response.data);
}

/** @see AppleBrandedPrograms.Web.Features.Catalog.ProductsExcelController */
export function getProductManagementTemplateUrl(): string {
	return '/api/management/products/bulk/excel/generate';
}

export type ProductExcelResult = ExcelResult<
	AppleBrandedProduct,
	'itemId',
	ProductExcelResultError
>;
export type ProductExcelResultError = ExcelError<AppleBrandedProduct, 'itemId'>;

export const productExcelErrorSchema = createExcelErrorSchema(
	excelProductKeySchema,
) satisfies z.ZodType<ProductExcelResultError>;

export const productExcelResultSchema = createExcelResultSchema(
	appleBrandedProductSchema,
	productExcelErrorSchema,
	excelProductKeySchema.shape.itemId,
) satisfies z.ZodType<ProductExcelResult>;
